import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import dayjs from 'dayjs'

import { CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { IDateTimeColumnSettings } from '@/core/models/columns/dateTime'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { ComponentSettingKey } from '@/shared/builder/types'
import { getSafely } from '@/shared/safely'
import { isEmpty } from '@/shared/utils'
import { useUpdateFormDefaultValue } from '@/components/__project__/hooks/useUpdateFormDefaultValue'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompDateTimePickerProps } from './comp'

const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6]

const getExcludedDays = (daysAllowed: number[]) => {
  if (daysAllowed.length === 0) {
    return '[]'
  }
  const excludedDays = DAYS_OF_WEEK.filter((day) => !daysAllowed.includes(day))
  return `[${excludedDays.join(',')}]`
}

export interface CompDateTimePickerWrapperProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  children: (props: CompDateTimePickerProps) => React.ReactNode
}

/**
 * CompDateTimePickerWrapper
 */
export const CompDateTimePickerWrapperCore = React.memo(
  ({
    infos: { settings, containerClient, detailTableId, detailRowIndex, component },
    form: { formSettings, formState, setFormState, dataSourceTable, setRequiredFieldStatus },
    children,
  }: CompDateTimePickerWrapperProps) => {
    const { isPhonePortrait, isTabletPortrait } = useBreakpoint()
    const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()

    const handleClick = () => {
      openComponentSettings(component)
      if (isDevelopmentMode) return
    }

    // ========================= data =========================
    const columnId = settings?.[ComponentSettingKey.DateTimePickerDataValue] as string
    const column = dataSourceTable?.columnsMap.get(columnId)
    const columnSettings = column?._Settings as unknown as IDateTimeColumnSettings
    const dtDisplay = columnSettings?.dtDisplay ?? 'datetime'

    const defaultValue = useParseVariableSelect<string>(
      settings[ComponentSettingKey.DateTimePickerDataDefaultValue],
      containerClient,
      detailTableId,
      detailRowIndex
    )

    const isEditable = getSafely(
      settings,
      [ComponentSettingKey.DateTimePickerDataDefaultValueEditable],
      true
    ) as boolean

    const mainLabelData = settings?.[ComponentSettingKey.DateTimePickerDataLabel]
    const mainLabel = useParseVariableText(mainLabelData, containerClient, detailTableId, detailRowIndex)

    // ========================= design =========================
    // NOTE: https://trello.com/c/kXZh15st/771-bug-on-analog-time-selection
    const timePickerVariant = 'chips' // getSafely(settings, [ComponentSettingKey.DateTimePickerDesignTimePickerVariant], 'chips')

    // ========================= behavior =========================
    const showTime = dtDisplay !== 'date'
    const required = getSafely(settings, [ComponentSettingKey.DateTimePickerBehaviorRequired], false)
    const daysAllowed = getSafely(settings, [ComponentSettingKey.DateTimePickerBehaviorDaysAllowed], DAYS_OF_WEEK)
    const futureDateOnly = getSafely(settings, [ComponentSettingKey.DateTimePickerBehaviorFutureDateOnly], false)
    const timeInterval = getSafely(settings, [ComponentSettingKey.DateTimePickerBehaviorTimeInterval], 15)
    const startTime = getSafely(settings, [ComponentSettingKey.DateTimePickerBehaviorStartTime], '00:00')
    const endTime = getSafely(settings, [ComponentSettingKey.DateTimePickerBehaviorEndTime], '24:00')

    // ========================= derived =========================
    const dateTimeMode = dtDisplay === 'time' ? 'time' : dtDisplay === 'date' ? 'date' : 'datetime'
    // ========================= form state =========================

    const timezone = dataSourceTable?.settings._Tz || 'auto'

    const rawValue = formState[columnId]
    const value = useMemo(() => rawValue as string | null, [rawValue])
    const prevValueRef = useRef(value)

    const handleChange = useCallback(
      (val: string | null) => {
        if (!columnId) return

        setFormState((prev) => ({ ...prev, [columnId]: val }))
      },
      [columnId, setFormState]
    )

    const handleUpdateDefaultValue = useCallback(
      (defValue: string | undefined) => {
        if (!columnId) return

        const parsedValue = defValue && dayjs(defValue).isValid() ? dayjs(defValue).toISOString() : null
        setFormState((prev) => ({ ...prev, [columnId]: parsedValue }))
      },
      [columnId, setFormState]
    )

    useUpdateFormDefaultValue({
      defaultValue,
      value: formState[columnId],
      onUpdate: handleUpdateDefaultValue,
    })

    // Add effect to update required field status
    useEffect(() => {
      if (required && value !== prevValueRef.current) {
        const isEmptyValue = !value && isEmpty(value)
        setRequiredFieldStatus(columnId, isEmptyValue)
        prevValueRef.current = value
      }
      return () => {
        if (required) {
          setRequiredFieldStatus(columnId, false)
        }
      }
    }, [required, value, columnId, setRequiredFieldStatus])

    const screenSize = useMemo(() => {
      if (isPhonePortrait || isTabletPortrait) {
        return 'mobile'
      }
      return 'desktop'
    }, [isPhonePortrait, isTabletPortrait])

    // ========================= render =========================
    const componentProps: CompDateTimePickerProps = {
      required,
      disabled: !isEditable,
      timezone,
      value: value as any,
      onChange: handleChange,
      showTime,
      label: mainLabel,
      interval: Number(timeInterval),
      startTime: startTime,
      endTime: endTime,
      excludedDays: getExcludedDays(daysAllowed) as any,
      minDate: futureDateOnly ? dayjs().startOf('day').toDate() : undefined,
      backgroundColor: formSettings.bgColor,
      cornerRadius: formSettings.cornerRadius,
      timePickerVariant: timePickerVariant as 'chips' | 'analog' | undefined,
      showDateInTimePicker: false,
      mode: dateTimeMode,
      screenSize: screenSize as 'desktop' | 'mobile' | undefined,
    }

    if (isDevelopmentMode) {
      return (
        <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
          {children(componentProps)}
          <CompOverlayDevmode handleClick={handleClick} />
        </div>
      )
    }

    return children(componentProps)
  }
)

CompDateTimePickerWrapperCore.displayName = 'CompDateTimePickerWrapperCore'
